import { useEffect, useMemo, useState } from "react"
import { Check, ChevronDown, Download, Loader2, Pencil, Plus, Trash2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { DatePopover } from "@/components/date-popover"
import { LockedBanner } from "@/features/transit/components/locked-banner"
import { SectionTitle } from "@/features/transit/components/section-title"
import { UploadButton } from "@/features/transit/components/upload-button"
import { UploadMetadataDialog } from "@/features/transit/components/upload-metadata-dialog"
import { UploadedDocRow } from "@/features/transit/components/uploaded-doc-row"
import {
  useCreateContainer,
  useDeleteContainer,
  usePatchContainer,
} from "@/features/transit/hooks/use-container-mutations"
import {
  useDeleteEmpotageFile,
  useDeletePhoto,
  useRegisterEmpotageFile,
  useRegisterPhoto,
} from "@/features/transit/hooks/use-doc-mutations"
import { useTransitUpload } from "@/features/transit/hooks/use-transit-upload"
import { useUploadWithMetadata } from "@/features/transit/hooks/use-upload-with-metadata"
import { usePatchOT } from "@/features/transit/hooks/use-transit-mutations"
import {
  containerNetReal,
  containerNetTheoretical,
  ecartFreinte,
  lotsTotalKg,
  otTotalNetReal,
  otTotalNetTheoretical,
  type ContainerWeightInputs,
} from "@/features/transit/lib/empotage-math"
import {
  buildEmpotageRapport,
  hasAnyWeight,
} from "@/features/transit/lib/empotage-rapport"
import { downloadEmpotageRapportPdf } from "@/features/transit/lib/empotage-rapport-pdf"
import { resolveApiError } from "@/lib/api/error-i18n"
import { isOTLocked } from "@/features/transit/lib/ot-math"
import { cn } from "@/lib/utils"
import type {
  ContainerType,
  EmpotageFileItem,
  EmpotageFileKind,
  EmpotageMode,
  OT,
  OTContainer,
} from "@/features/transit/api/types"

const EMPOTAGE_MODES: EmpotageMode[] = ["vracs", "conventionnel"]

// Mirror of the backend `ContainerTypeForMode` map. Persisted server-side
// too, but kept here so the FE can render the derived value while a row
// is still being edited.
function containerTypeForMode(mode: EmpotageMode | "" | null): ContainerType | null {
  if (mode === "vracs") return "20"
  if (mode === "conventionnel") return "40"
  return null
}

// Backend serialises `date` columns as ISO timestamps in some paths;
// the row's stuffingDate can land here as either "YYYY-MM-DD" or a
// full "YYYY-MM-DDT00:00:00+00:00". Format it locale-aware for display.
function formatStuffingDate(value: string | null | undefined, locale: string): string {
  if (!value) return ""
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value.slice(0, 10)
  try {
    return d.toLocaleDateString(locale, { dateStyle: "medium" })
  } catch {
    return value.slice(0, 10)
  }
}

interface EmpotagePanelProps {
  ot: OT
}

export function EmpotagePanel({ ot }: EmpotagePanelProps) {
  const locked = isOTLocked(ot)
  return (
    <div className="flex flex-col gap-4">
      {locked ? <LockedBanner ot={ot} /> : null}
      <ContainersCard ot={ot} />
      <PhotosCard ot={ot} />
      <DocumentsCard ot={ot} />
    </div>
  )
}

// ── Containers ─────────────────────────────────────────────────────

interface RowDraft {
  mode: EmpotageMode | ""
  containerNumber: string
  plombNumber: string
  stuffingDate: string
  loaded: string
  empty: string
  dressing: string
  theoreticalTare: string
  realTare: string
  observation: string
}

function rowFromContainer(c: OTContainer): RowDraft {
  return {
    mode: (c.empotageMode ?? "") as EmpotageMode | "",
    containerNumber: c.containerNumber ?? "",
    plombNumber: c.plombNumber ?? "",
    stuffingDate: c.stuffingDate ?? "",
    loaded: numToInput(c.loadedKg),
    empty: numToInput(c.emptyKg),
    dressing: numToInput(c.dressingKg),
    theoreticalTare: numToInput(c.theoreticalTareKg),
    realTare: numToInput(c.realTareKg),
    observation: c.observation ?? "",
  }
}

function rowsEqual(a: RowDraft, b: RowDraft): boolean {
  return (
    a.mode === b.mode &&
    a.containerNumber === b.containerNumber &&
    a.plombNumber === b.plombNumber &&
    a.stuffingDate === b.stuffingDate &&
    a.loaded === b.loaded &&
    a.empty === b.empty &&
    a.dressing === b.dressing &&
    a.theoreticalTare === b.theoreticalTare &&
    a.realTare === b.realTare &&
    a.observation === b.observation
  )
}

function numToInput(v: number | null | undefined): string {
  if (v == null) return ""
  return String(v)
}

// Convert input string to a numeric (number | null | undefined) tuple
// the mutation layer expects. Empty strings clear the field on patch
// (PATCH payloads explicitly send null); on create we use `undefined`.
function parseInputForPatch(s: string): number | null {
  const trimmed = s.trim()
  if (trimmed === "") return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : null
}
function parseInputForCreate(s: string): number | undefined {
  const trimmed = s.trim()
  if (trimmed === "") return undefined
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : undefined
}

// Two-shape target for the editor dialog: "new" mounts an empty draft,
// "edit" seeds from an existing row. Closing the dialog clears state.
type EditorTarget =
  | { kind: "new" }
  | { kind: "edit"; container: OTContainer }
  | null

function ContainersCard({ ot }: { ot: OT }) {
  const { t } = useTranslation("transit")
  const remove = useDeleteContainer(ot.id)
  const patchOT = usePatchOT(ot.id)
  const locked = isOTLocked(ot)

  // Sort by id so editing one container doesn't reshuffle the layout
  // when PostgREST returns the embed in a different order after update.
  const containers = useMemo(
    () => [...(ot.containers ?? [])].sort((a, b) => a.id - b.id),
    [ot.containers],
  )

  const [editorTarget, setEditorTarget] = useState<EditorTarget>(null)
  const [expanded, setExpanded] = useState<Record<number, boolean>>({})
  const toggleExpanded = (id: number) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))

  // OT-level rollups read from the saved state. Edits route through the
  // dialog and only land here once committed — keeps the BL comparison
  // stable across the typing window.
  const totalNetTheoretical = useMemo(
    () => otTotalNetTheoretical(containers),
    [containers],
  )
  const totalNetReal = useMemo(() => otTotalNetReal(containers), [containers])

  // `totalStuffedWeightKg` powers the BL mismatch warning. Mirror the
  // theoretical-tare net to it — that's the number that goes onto the
  // BL by convention. Legacy single-number rows still feed in via the
  // weighingWeightKg fallback inside containerNet*.
  useEffect(() => {
    if (locked) return
    const next = totalNetTheoretical
    if (next === (ot.totalStuffedWeightKg ?? null)) return
    if (patchOT.isPending) return
    patchOT.mutate({ totalStuffedWeightKg: next })
  }, [locked, totalNetTheoretical, ot.totalStuffedWeightKg, patchOT])

  const engageKg = useMemo(() => lotsTotalKg(ot.lots ?? []), [ot.lots])
  const theoreticalFreinte = ecartFreinte(engageKg, totalNetTheoretical)
  const realFreinte = ecartFreinte(engageKg, totalNetReal)

  return (
    <Card className="flex flex-col gap-4 p-5">
      <SectionTitle
        eyebrow={t("empotage.eyebrow")}
        title={t("empotage.containersTitle")}
        description={t("empotage.containersDescription")}
        actions={
          locked ? null : (
            <Button size="sm" onClick={() => setEditorTarget({ kind: "new" })}>
              <Plus className="size-4" />
              {t("empotage.addContainer")}
            </Button>
          )
        }
      />

      {containers.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed py-6 text-center text-sm">
          {t("empotage.noContainers")}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {containers.map((c) => (
            <ContainerRow
              key={c.id}
              container={c}
              expanded={!!expanded[c.id]}
              deleting={remove.isPending}
              locked={locked}
              onToggleExpanded={() => toggleExpanded(c.id)}
              onEdit={() => setEditorTarget({ kind: "edit", container: c })}
              onDelete={() => remove.mutate(c.id)}
            />
          ))}
        </div>
      )}

      <Rollups
        engageKg={engageKg}
        totalNetTheoreticalKg={totalNetTheoretical}
        totalNetRealKg={totalNetReal}
        theoreticalFreinte={theoreticalFreinte}
        realFreinte={realFreinte}
      />

      <ContainerEditorDialog
        otId={ot.id}
        target={editorTarget}
        onClose={() => setEditorTarget(null)}
      />
    </Card>
  )
}

interface ContainerRowProps {
  container: OTContainer
  expanded: boolean
  deleting: boolean
  locked: boolean
  onToggleExpanded: () => void
  onEdit: () => void
  onDelete: () => void
}

function ContainerRow({
  container,
  expanded,
  deleting,
  locked,
  onToggleExpanded,
  onEdit,
  onDelete,
}: ContainerRowProps) {
  const { t, i18n } = useTranslation("transit")
  const typeKey =
    (container.containerType as ContainerType | null | undefined) ??
    containerTypeForMode(
      (container.empotageMode as EmpotageMode | null | undefined) ?? "",
    )
  const inputs = {
    loadedKg: container.loadedKg ?? null,
    emptyKg: container.emptyKg ?? null,
    theoreticalTareKg: container.theoreticalTareKg ?? null,
    realTareKg: container.realTareKg ?? null,
    dressingKg: container.dressingKg ?? null,
    weighingWeightKg: container.weighingWeightKg ?? null,
  }
  const netTheoreticalKg = containerNetTheoretical(inputs)
  const netRealKg = containerNetReal(inputs)

  return (
    <div className="rounded-md border bg-card">
      <div className="flex items-center gap-2 px-3 py-2">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onToggleExpanded}
          aria-label={t("empotage.expandRow")}
        >
          <ChevronDown
            className={cn(
              "size-4 transition-transform",
              expanded ? "rotate-0" : "-rotate-90",
            )}
          />
        </Button>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
            <span className="font-mono font-medium">
              {container.containerNumber ||
                t("empotage.placeholders.containerNumber")}
            </span>
            {typeKey ? (
              <span className="text-muted-foreground text-[11px]">
                · {t(`empotage.type.${typeKey}`)}
              </span>
            ) : null}
            {container.plombNumber ? (
              <span className="text-muted-foreground text-[11px] font-mono">
                · {container.plombNumber}
              </span>
            ) : null}
            {container.stuffingDate ? (
              <span className="text-muted-foreground text-[11px]">
                · {formatStuffingDate(container.stuffingDate, i18n.language)}
              </span>
            ) : null}
          </div>
          {netTheoreticalKg != null ? (
            <div className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
              {t("empotage.row.netTheoretical")}:{" "}
              <span className="text-foreground font-medium">
                {netTheoreticalKg.toLocaleString()} kg
              </span>
              {netRealKg != null && netRealKg !== netTheoreticalKg ? (
                <>
                  {" · "}
                  {t("empotage.row.netReal")}:{" "}
                  <span className="text-foreground font-medium">
                    {netRealKg.toLocaleString()} kg
                  </span>
                </>
              ) : null}
            </div>
          ) : null}
        </div>

        {locked ? null : (
          <div className="flex flex-shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onEdit}
              aria-label={t("empotage.editRow")}
            >
              <Pencil className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onDelete}
              disabled={deleting}
              aria-label={t("docCard.delete")}
              className="hover:text-red-600"
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        )}
      </div>

      {expanded ? (
        <div className="border-t bg-muted/30 px-4 py-3">
          <ContainerReadonly container={container} />
        </div>
      ) : null}
    </div>
  )
}

function ContainerForm({
  draft,
  onChange,
}: {
  draft: RowDraft
  onChange: (next: RowDraft) => void
}) {
  const { t } = useTranslation("transit")
  const derivedType = containerTypeForMode(draft.mode)

  // Live nets reflect what the user just typed — we keep them in sync
  // by feeding the draft straight into the same math helpers the saved
  // row uses. Empty strings (unfilled fields) leave the net null.
  const inputs: ContainerWeightInputs = {
    loadedKg: parseInputForCreate(draft.loaded) ?? null,
    emptyKg: parseInputForCreate(draft.empty) ?? null,
    theoreticalTareKg: parseInputForCreate(draft.theoreticalTare) ?? null,
    realTareKg: parseInputForCreate(draft.realTare) ?? null,
    dressingKg: parseInputForCreate(draft.dressing) ?? null,
  }
  const liveNetTh = containerNetTheoretical(inputs)
  const liveNetRe = containerNetReal(inputs)

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <FormField label={t("empotage.cols.mode")} required>
        <Select
          value={draft.mode}
          onValueChange={(v) =>
            onChange({ ...draft, mode: v as EmpotageMode })
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t("empotage.placeholders.mode")} />
          </SelectTrigger>
          <SelectContent>
            {EMPOTAGE_MODES.map((m) => (
              <SelectItem key={m} value={m}>
                {t(`empotage.mode.${m}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      {/* Derived from mode (vracs → 20', conventionnel → 40'); read-only
          so the operator can verify the pairing without picking it. */}
      <FormField label={t("empotage.cols.type")}>
        <Input
          value={derivedType ? t(`empotage.type.${derivedType}`) : ""}
          placeholder={t("empotage.placeholders.type")}
          readOnly
          disabled
          className="disabled:opacity-100"
        />
      </FormField>

      <FormField label={t("empotage.cols.container")}>
        <Input
          value={draft.containerNumber}
          onChange={(e) =>
            onChange({ ...draft, containerNumber: e.target.value })
          }
          placeholder={t("empotage.placeholders.containerNumber")}
          className="font-mono"
        />
      </FormField>

      <FormField label={t("empotage.cols.plomb")}>
        <Input
          value={draft.plombNumber}
          onChange={(e) =>
            onChange({ ...draft, plombNumber: e.target.value })
          }
          placeholder={t("empotage.placeholders.plombNumber")}
          className="font-mono"
        />
      </FormField>

      <FormField label={t("empotage.cols.date")}>
        <DatePopover
          value={draft.stuffingDate}
          onChange={(iso) => onChange({ ...draft, stuffingDate: iso })}
        />
      </FormField>

      {/* Spacer so the date sits opposite an empty cell — keeps the
          weighing block visually grouped on the row below. */}
      <div className="hidden md:block" />

      <FormField label={t("empotage.weights.loaded")} hint={t("empotage.weights.loadedHint")}>
        <WeightInput
          value={draft.loaded}
          onChange={(v) => onChange({ ...draft, loaded: v })}
        />
      </FormField>

      <FormField label={t("empotage.weights.empty")} hint={t("empotage.weights.emptyHint")}>
        <WeightInput
          value={draft.empty}
          onChange={(v) => onChange({ ...draft, empty: v })}
        />
      </FormField>

      <FormField
        label={t("empotage.weights.dressing")}
        hint={t("empotage.weights.dressingHint")}
      >
        <WeightInput
          value={draft.dressing}
          onChange={(v) => onChange({ ...draft, dressing: v })}
        />
      </FormField>

      <FormField
        label={t("empotage.weights.theoreticalTare")}
        hint={t("empotage.weights.theoreticalTareHint")}
      >
        <WeightInput
          value={draft.theoreticalTare}
          onChange={(v) => onChange({ ...draft, theoreticalTare: v })}
        />
      </FormField>

      <FormField
        label={t("empotage.weights.realTare")}
        hint={t("empotage.weights.realTareHint")}
      >
        <WeightInput
          value={draft.realTare}
          onChange={(v) => onChange({ ...draft, realTare: v })}
        />
      </FormField>

      {/* Read-only derived nets — kept inside the dialog grid so the
          operator can confirm the math before saving. Show "—" while
          inputs are still missing rather than zero. */}
      <FormField label={t("empotage.weights.netTheoretical")}>
        <ComputedWeight value={liveNetTh} />
      </FormField>

      <FormField label={t("empotage.weights.netReal")}>
        <ComputedWeight value={liveNetRe} />
      </FormField>

      <div className="md:col-span-2">
        <FormField label={t("empotage.observation")}>
          <Textarea
            value={draft.observation}
            onChange={(e) =>
              onChange({ ...draft, observation: e.target.value })
            }
            rows={2}
            placeholder={t("empotage.observationPlaceholder")}
          />
        </FormField>
      </div>
    </div>
  )
}

function ComputedWeight({ value }: { value: number | null }) {
  return (
    <div className="bg-muted/40 text-foreground border-input flex h-9 items-center justify-between rounded-md border px-3 text-sm tabular-nums">
      <span>{value != null ? value.toLocaleString() : "—"}</span>
      <span className="text-muted-foreground text-xs">kg</span>
    </div>
  )
}

// Editor for both "add" and "edit". The shell stays mounted with the
// Dialog so it can animate the close transition; the body is keyed on
// the target so React re-mounts its local draft state for each new
// open — no useEffect re-seed needed.
function ContainerEditorDialog({
  otId,
  target,
  onClose,
}: {
  otId: number | string
  target: EditorTarget
  onClose: () => void
}) {
  const open = target != null
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className="sm:max-w-2xl">
        {target ? (
          <ContainerEditorBody
            key={target.kind === "edit" ? `edit-${target.container.id}` : "new"}
            otId={otId}
            target={target}
            onClose={onClose}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function ContainerEditorBody({
  otId,
  target,
  onClose,
}: {
  otId: number | string
  target: Exclude<EditorTarget, null>
  onClose: () => void
}) {
  const { t } = useTranslation("transit")
  const create = useCreateContainer(otId)
  const patch = usePatchContainer(otId)
  const isEdit = target.kind === "edit"
  const [draft, setDraft] = useState<RowDraft>(() =>
    isEdit
      ? rowFromContainer(target.container)
      : {
          mode: "",
          containerNumber: "",
          plombNumber: "",
          stuffingDate: new Date().toISOString().slice(0, 10),
          loaded: "",
          empty: "",
          dressing: "",
          theoreticalTare: "",
          realTare: "",
          observation: "",
        },
  )

  const submitting = create.isPending || patch.isPending
  const isDirty = isEdit
    ? !rowsEqual(draft, rowFromContainer(target.container))
    : true
  const canSubmit = draft.mode !== "" && !submitting && (isEdit ? isDirty : true)

  const onSubmit = () => {
    if (!canSubmit) return
    if (isEdit) {
      const c = target.container
      const server = rowFromContainer(c)
      const payload: Record<string, unknown> = {}
      if (draft.mode !== server.mode) payload.empotageMode = draft.mode
      if (draft.containerNumber !== server.containerNumber)
        payload.containerNumber = draft.containerNumber
      if (draft.plombNumber !== server.plombNumber)
        payload.plombNumber = draft.plombNumber
      if (draft.stuffingDate !== server.stuffingDate)
        payload.stuffingDate = draft.stuffingDate
      if (draft.loaded !== server.loaded)
        payload.loadedKg = parseInputForPatch(draft.loaded)
      if (draft.empty !== server.empty)
        payload.emptyKg = parseInputForPatch(draft.empty)
      if (draft.theoreticalTare !== server.theoreticalTare)
        payload.theoreticalTareKg = parseInputForPatch(draft.theoreticalTare)
      if (draft.realTare !== server.realTare)
        payload.realTareKg = parseInputForPatch(draft.realTare)
      if (draft.dressing !== server.dressing)
        payload.dressingKg = parseInputForPatch(draft.dressing)
      if (draft.observation !== server.observation)
        payload.observation = draft.observation
      if (Object.keys(payload).length === 0) {
        onClose()
        return
      }
      patch.mutate(
        { containerId: c.id, payload },
        { onSuccess: onClose },
      )
      return
    }
    create.mutate(
      {
        empotageMode: draft.mode === "" ? undefined : draft.mode,
        containerNumber: draft.containerNumber || undefined,
        plombNumber: draft.plombNumber || undefined,
        stuffingDate: draft.stuffingDate || undefined,
        loadedKg: parseInputForCreate(draft.loaded),
        emptyKg: parseInputForCreate(draft.empty),
        theoreticalTareKg: parseInputForCreate(draft.theoreticalTare),
        realTareKg: parseInputForCreate(draft.realTare),
        dressingKg: parseInputForCreate(draft.dressing),
        observation: draft.observation.trim() || undefined,
      },
      { onSuccess: onClose },
    )
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {isEdit
            ? t("empotage.editorDialog.editTitle")
            : t("empotage.editorDialog.newTitle")}
        </DialogTitle>
        <DialogDescription>
          {t("empotage.editorDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <ContainerForm draft={draft} onChange={setDraft} />

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>
          {t("wizard.cancel")}
        </Button>
        <Button onClick={onSubmit} disabled={!canSubmit}>
          {submitting ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Check className="size-4" />
          )}
          {isEdit
            ? t("empotage.editorDialog.saveEdit")
            : t("empotage.editorDialog.saveNew")}
        </Button>
      </DialogFooter>
    </>
  )
}

function ContainerReadonly({
  container,
}: {
  container: OTContainer | undefined
}) {
  const { t } = useTranslation("transit")
  if (!container) return null
  const netTh = containerNetTheoretical({
    loadedKg: container.loadedKg ?? null,
    emptyKg: container.emptyKg ?? null,
    theoreticalTareKg: container.theoreticalTareKg ?? null,
    dressingKg: container.dressingKg ?? null,
    weighingWeightKg: container.weighingWeightKg ?? null,
  })
  const netRe = containerNetReal({
    loadedKg: container.loadedKg ?? null,
    emptyKg: container.emptyKg ?? null,
    realTareKg: container.realTareKg ?? null,
    dressingKg: container.dressingKg ?? null,
    weighingWeightKg: container.weighingWeightKg ?? null,
  })
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs md:grid-cols-4">
      <ReadonlyStat
        label={t("empotage.weights.loaded")}
        value={container.loadedKg ?? null}
      />
      <ReadonlyStat
        label={t("empotage.weights.empty")}
        value={container.emptyKg ?? null}
      />
      <ReadonlyStat
        label={t("empotage.weights.dressing")}
        value={container.dressingKg ?? null}
      />
      <ReadonlyStat
        label={t("empotage.weights.theoreticalTare")}
        value={container.theoreticalTareKg ?? null}
      />
      <ReadonlyStat
        label={t("empotage.weights.realTare")}
        value={container.realTareKg ?? null}
      />
      <ReadonlyStat
        label={t("empotage.row.netTheoretical")}
        value={netTh}
        emphasised
      />
      <ReadonlyStat
        label={t("empotage.row.netReal")}
        value={netRe}
        emphasised
      />
      {container.observation ? (
        <div className="col-span-2 md:col-span-4">
          <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
            {t("empotage.observation")}
          </div>
          <div className="text-foreground whitespace-pre-wrap">
            {container.observation}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function ReadonlyStat({
  label,
  value,
  emphasised,
}: {
  label: string
  value: number | null
  emphasised?: boolean
}) {
  return (
    <div>
      <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
        {label}
      </div>
      <div
        className={cn(
          "tabular-nums",
          emphasised ? "text-foreground font-semibold" : "text-foreground",
        )}
      >
        {value != null ? `${value.toLocaleString()} kg` : "—"}
      </div>
    </div>
  )
}

function FormField({
  label,
  hint,
  required,
  children,
}: {
  label: string
  hint?: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {hint ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Label className="cursor-help">
              {label}
              {required ? <span className="text-red-500"> *</span> : null}
            </Label>
          </TooltipTrigger>
          <TooltipContent>{hint}</TooltipContent>
        </Tooltip>
      ) : (
        <Label>
          {label}
          {required ? <span className="text-red-500"> *</span> : null}
        </Label>
      )}
      {children}
    </div>
  )
}

function WeightInput({
  value,
  onChange,
}: {
  value: string
  onChange: (next: string) => void
}) {
  return (
    <div className="relative">
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pr-10 tabular-nums"
      />
      <span className="text-muted-foreground absolute top-1/2 right-3 -translate-y-1/2 text-xs">
        kg
      </span>
    </div>
  )
}

function Rollups({
  engageKg,
  totalNetTheoreticalKg,
  totalNetRealKg,
  theoreticalFreinte,
  realFreinte,
}: {
  engageKg: number
  totalNetTheoreticalKg: number | null
  totalNetRealKg: number | null
  theoreticalFreinte: { ecart: number; freintePct: number } | null
  realFreinte: { ecart: number; freintePct: number } | null
}) {
  const { t } = useTranslation("transit")
  return (
    <div className="border-border/60 border-t pt-4">
      <SectionTitle
        title={t("empotage.rollupsTitle")}
        description={t("empotage.rollupsDescription")}
      />
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <RollupStat
          label={t("empotage.rollups.engage")}
          value={engageKg ? `${(engageKg / 1000).toFixed(3)} MT` : "—"}
          sub={engageKg ? `${Math.round(engageKg).toLocaleString()} kg` : null}
        />
        <RollupStat
          label={t("empotage.rollups.totalNetTheoretical")}
          value={
            totalNetTheoreticalKg != null
              ? `${(totalNetTheoreticalKg / 1000).toFixed(3)} MT`
              : "—"
          }
          sub={
            totalNetTheoreticalKg != null
              ? `${totalNetTheoreticalKg.toLocaleString()} kg`
              : null
          }
        />
        <RollupStat
          label={t("empotage.rollups.totalNetReal")}
          value={
            totalNetRealKg != null
              ? `${(totalNetRealKg / 1000).toFixed(3)} MT`
              : "—"
          }
          sub={
            totalNetRealKg != null
              ? `${totalNetRealKg.toLocaleString()} kg`
              : null
          }
        />
        <RollupStat
          label={t("empotage.rollups.ecartTheoretical")}
          value={
            theoreticalFreinte
              ? `${theoreticalFreinte.ecart >= 0 ? "+" : "−"}${Math.abs(
                  theoreticalFreinte.ecart,
                ).toLocaleString()} kg`
              : "—"
          }
          sub={
            theoreticalFreinte
              ? `${t("empotage.rollups.freinte")}: ${theoreticalFreinte.freintePct.toFixed(3)} %`
              : null
          }
        />
        <RollupStat
          label={t("empotage.rollups.ecartReal")}
          value={
            realFreinte
              ? `${realFreinte.ecart >= 0 ? "+" : "−"}${Math.abs(
                  realFreinte.ecart,
                ).toLocaleString()} kg`
              : "—"
          }
          sub={
            realFreinte
              ? `${t("empotage.rollups.freinte")}: ${realFreinte.freintePct.toFixed(3)} %`
              : null
          }
        />
      </div>
    </div>
  )
}

function RollupStat({
  label,
  value,
  sub,
}: {
  label: string
  value: string
  sub: string | null
}) {
  return (
    <div className="bg-muted/40 flex flex-col gap-0.5 rounded-md border px-3 py-2">
      <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
        {label}
      </div>
      <div className="text-foreground text-sm font-semibold tabular-nums">
        {value}
      </div>
      {sub ? (
        <div className="text-muted-foreground text-[11px] tabular-nums">
          {sub}
        </div>
      ) : null}
    </div>
  )
}

// ── Photos ─────────────────────────────────────────────────────────

function PhotosCard({ ot }: { ot: OT }) {
  const { t } = useTranslation("transit")
  const upload = useTransitUpload()
  const register = useRegisterPhoto(ot.id)
  const remove = useDeletePhoto(ot.id)
  const photos = ot.photos ?? []
  const locked = isOTLocked(ot)

  const meta = useUploadWithMetadata({
    kindLabel: t("empotage.photosTitle"),
    upload: async (file, m) => {
      await upload.upload({
        otId: ot.id,
        kind: "photo",
        file,
        register: async ({ storedKey, file: f }) =>
          register.mutateAsync({
            name: m.name,
            storedKey,
            docType: f.type || undefined,
            uploadedBy: m.uploadedBy || undefined,
            note: m.description || undefined,
          }),
      })
    },
  })

  return (
    <Card className="flex flex-col gap-3 p-5">
      <SectionTitle
        eyebrow={t("empotage.photosEyebrow")}
        title={t("empotage.photosTitle")}
        description={t("empotage.photosDescription")}
        actions={
          locked ? null : (
            <UploadButton
              onPick={meta.onPick}
              accept="image/*"
              isUploading={upload.isUploading || register.isPending}
              size="sm"
              label={t("empotage.photosAdd")}
            />
          )
        }
      />

      {photos.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed py-6 text-center text-sm">
          {t("empotage.photosEmpty")}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {photos.map((p) => (
            <UploadedDocRow
              key={p.id}
              otId={ot.id}
              doc={p}
              onDelete={() => remove.mutate(p.id)}
              isDeleting={remove.isPending}
              locked={locked}
            />
          ))}
        </div>
      )}

      <UploadMetadataDialog
        pending={meta.pending}
        onCancel={meta.onCancel}
        onConfirm={meta.onConfirm}
      />
    </Card>
  )
}

// ── Documents (tickets de pesée + autres) ──────────────────────────
//
// CADESA OT workshop: the manual rapport d'empotage upload was removed.
// The rapport is now generated server-side from the container weighing
// math + photos — when that endpoint lands, an "Exporter le rapport"
// button takes its place here.

function DocumentsCard({ ot }: { ot: OT }) {
  const { t } = useTranslation("transit")
  const files = ot.empotageFiles ?? []
  const tickets = files.filter((f) => f.kind === "ticketPesee")
  const others = files.filter(
    (f) => f.kind !== "rapportEmpotage" && f.kind !== "ticketPesee",
  )

  return (
    <Card className="flex flex-col gap-5 p-5">
      <SectionTitle
        eyebrow={t("empotage.docsEyebrow")}
        title={t("empotage.docsTitle")}
        description={t("empotage.docsDescription")}
      />

      <RapportSection ot={ot} />
      <TicketsSection ot={ot} tickets={tickets} />
      <OtherSection ot={ot} others={others} />
    </Card>
  )
}

// Replaces the legacy manual rapport upload: the operator clicks
// "Télécharger le rapport" and we generate the PDF in-browser. Disabled
// until at least one container carries weights — saves shipping a
// blank PDF the operator can't use anyway.
function RapportSection({ ot }: { ot: OT }) {
  const { t } = useTranslation("transit")
  const [generating, setGenerating] = useState(false)

  const rapport = useMemo(() => buildEmpotageRapport(ot), [ot])
  const ready = hasAnyWeight(rapport)

  const handleDownload = async () => {
    if (generating || !ready) return
    setGenerating(true)
    try {
      await downloadEmpotageRapportPdf(rapport, ot.id, t)
    } catch (err) {
      toast.error(resolveApiError(err, t))
    } finally {
      setGenerating(false)
    }
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-foreground text-sm font-medium">
            {t("empotage.rapportTitle")}
          </div>
          <p className="text-muted-foreground text-xs">
            {t("empotage.rapportDescription")}
          </p>
        </div>
        <Button size="sm" onClick={handleDownload} disabled={!ready || generating}>
          {generating ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Download className="size-4" />
          )}
          {t("empotage.rapportDownload")}
        </Button>
      </div>
      {!ready ? (
        <p className="text-muted-foreground text-[11px]">
          {t("empotage.rapportPendingHint")}
        </p>
      ) : null}
    </section>
  )
}

function TicketsSection({
  ot,
  tickets,
}: {
  ot: OT
  tickets: EmpotageFileItem[]
}) {
  const { t } = useTranslation("transit")
  const remove = useDeleteEmpotageFile(ot.id)
  const locked = isOTLocked(ot)
  const upload = useEmpotageFileUpload(ot, "ticketPesee", {
    label: t("empotage.ticketsTitle"),
  })

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-foreground text-sm font-medium">
            {t("empotage.ticketsTitle")}{" "}
            <span className="text-red-500">*</span>{" "}
            <span className="text-muted-foreground text-xs font-normal">
              ({tickets.length})
            </span>
          </div>
          <p className="text-muted-foreground text-xs">
            {t("empotage.ticketsDescription")}
          </p>
        </div>
        {locked ? null : (
          <UploadButton
            onPick={upload.meta.onPick}
            isUploading={upload.isUploading}
            size="sm"
            label={t("empotage.ticketsAdd")}
          />
        )}
      </div>
      {tickets.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed py-4 text-center text-sm">
          {t("empotage.ticketsEmpty")}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {tickets.map((tic) => (
            <li key={tic.id}>
              <UploadedDocRow
                otId={ot.id}
                doc={tic}
                onDelete={() => remove.mutate(tic.id)}
                isDeleting={remove.isPending}
                locked={locked}
              />
            </li>
          ))}
        </ul>
      )}
      {upload.dialog}
    </section>
  )
}

function OtherSection({
  ot,
  others,
}: {
  ot: OT
  others: EmpotageFileItem[]
}) {
  const { t } = useTranslation("transit")
  const remove = useDeleteEmpotageFile(ot.id)
  const locked = isOTLocked(ot)
  const upload = useEmpotageFileUpload(ot, "other", {
    label: t("empotage.othersTitle"),
  })

  return (
    <section className="flex flex-col gap-2 border-t pt-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-foreground text-sm font-medium">
            {t("empotage.othersTitle")}{" "}
            <span className="text-muted-foreground text-xs font-normal">
              ({others.length})
            </span>
          </div>
          <p className="text-muted-foreground text-xs">
            {t("empotage.othersDescription")}
          </p>
        </div>
        {locked ? null : (
          <UploadButton
            onPick={upload.meta.onPick}
            isUploading={upload.isUploading}
            size="sm"
            label={t("empotage.othersAdd")}
          />
        )}
      </div>
      {others.length === 0 ? null : (
        <ul className="flex flex-col gap-2">
          {others.map((f) => (
            <li key={f.id}>
              <UploadedDocRow
                otId={ot.id}
                doc={f}
                onDelete={() => remove.mutate(f.id)}
                isDeleting={remove.isPending}
                locked={locked}
              />
            </li>
          ))}
        </ul>
      )}
      {upload.dialog}
    </section>
  )
}

// Internal helper — wires the metadata dialog + upload pipeline + the
// `register-empotage-file` mutation for a specific kind.
function useEmpotageFileUpload(
  ot: OT,
  kind: EmpotageFileKind,
  opts: { label: string },
) {
  const upload = useTransitUpload()
  const register = useRegisterEmpotageFile(ot.id)

  const meta = useUploadWithMetadata({
    kindLabel: opts.label,
    upload: async (file, m) => {
      await upload.upload({
        otId: ot.id,
        kind: "empotageFile",
        file,
        register: async ({ storedKey, file: f }) =>
          register.mutateAsync({
            name: m.name,
            storedKey,
            kind,
            docType: f.type || undefined,
            size: f.size,
            uploadedBy: m.uploadedBy || undefined,
            note: m.description || undefined,
          }),
      })
    },
  })

  return {
    meta,
    isUploading: upload.isUploading || register.isPending,
    dialog: (
      <UploadMetadataDialog
        pending={meta.pending}
        onCancel={meta.onCancel}
        onConfirm={meta.onConfirm}
      />
    ),
  }
}
